// 🔴 P-MARIO-03 — ENTRÉE V1 : résolution client → lieu → dossier, puis relevé.
//
// 🔑 FRONTIÈRE L1 (décision CTO P-MARIO-11) : `chantier` ⛔ ne lit PAS les clients,
//    lieux et dossiers d'un autre domaine. C'est L'ÉCRAN qui ORCHESTRE les routes
//    EXISTANTES de ces domaines, puis confie le dossier choisi à `/api/chantier/*`.
//    (`pages/chantier/` reste réservé à `/api/chantier/*` — d'où ce module à part.)
// 🔑 Session OmniHub : le tenant ⛔ n'est JAMAIS envoyé, le serveur le lit dans la session.
// ⛔ AUCUNE écriture tant que l'humain n'a pas confirmé : `chercher*` et `lieux*` sont
//    des LECTURES ; `confirmer` est le SEUL point d'écriture.
import { apiBureau } from "../chantier/api";
import { cleAdresse, trouverSiteEquivalent } from "../../utils/siteAdresse";

const API = process.env.REACT_APP_BACKEND_URL || "";
const e = encodeURIComponent;

function entetes(json) {
  let jeton = null;
  try { jeton = localStorage.getItem("session_token"); } catch { /* indisponible */ }
  return { ...(json ? { "Content-Type": "application/json" } : {}),
           ...(jeton ? { Authorization: `Bearer ${jeton}` } : {}) };
}

async function appel(chemin, { methode = "GET", corps } = {}) {
  const r = await fetch(`${API}${chemin}`, {
    method: methode, credentials: "include", headers: entetes(corps !== undefined),
    ...(corps !== undefined ? { body: JSON.stringify(corps) } : {}),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const d = j?.detail;
    const err = new Error(typeof d === "string" ? d : (d?.message || `Échec (HTTP ${r.status})`));
    err.statut = r.status;
    if (Array.isArray(d?.bloquants)) err.bloquants = d.bloquants;
    throw err;
  }
  return j;
}

/** Minuscules sans accents — pour COMPARER, ⛔ pas pour afficher. */
export function plat(t) {
  return String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}
const echapper = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Dictée « Client, adresse… » ⇒ { client, lieu } (1re virgule). Les deux champs restent
 *  MODIFIABLES : ce découpage est une proposition, ⛔ une décision. */
export function decouperDictee(texte) {
  const t = String(texte || "").trim();
  const i = t.indexOf(",");
  return i < 0 ? { client: t, lieu: "" } : { client: t.slice(0, i).trim(), lieu: t.slice(i + 1).trim() };
}

/** Clients qui correspondent (LECTURE, ⛔ aucune sélection ni création).
 *  🔴 N°1006-C — le nom DICTÉ part au serveur en `mode=dictee` : rapprochement tolérant
 *  (casse, accents, ponctuation, phonétique), candidats du SEUL tenant de la session,
 *  classés, avec `rapprochement` (exact | proche) et `decision` (unique | plusieurs). Aucun
 *  candidat fiable ⇒ liste vide ⇒ l'écran dit « aucun client ne correspond ».
 *  Compatibilité : un serveur qui ignore `mode` renvoie la recherche historique (sans
 *  `decision`) ⇒ on garde alors l'ancien repli « mot à mot ». */
export async function chercherClients(texte) {
  const q = String(texte || "").trim();
  if (q.length < 2) return [];
  const dictee = await appel(`/api/metier/clients/search?q=${e(q)}&limit=10&mode=dictee`);
  if (Array.isArray(dictee) && (dictee.length === 0 || dictee[0]?.decision)) return dictee;
  const vus = new Map();
  const garder = (liste) => (liste || []).forEach((c) => { if (c?.id && !vus.has(c.id)) vus.set(c.id, c); });
  garder(await appel(`/api/metier/clients/search?q=${e(echapper(q))}&limit=10`));
  if (!vus.size) {
    for (const mot of q.split(/[\s,]+/).filter((m) => m.length >= 3)) {
      garder(await appel(`/api/metier/clients/search?q=${e(echapper(mot))}&limit=10`));
    }
  }
  return [...vus.values()];
}

/** Pertinence d'un lieu pour ce qui a été dicté : nombre de mots retrouvés. */
export function scoreLieu(lieu, texte) {
  const cible = plat([lieu.nom_site, lieu.adresse, lieu.code_postal, lieu.ville].filter(Boolean).join(" "));
  return plat(texte).split(/[\s,]+/).filter((m) => m.length >= 2 && cible.includes(m)).length;
}

/** Lieux du client (LECTURE), chacun avec ses dossiers — les plus pertinents d'abord. */
export async function lieuxDuClient(clientId, texte) {
  const j = await appel(`/api/metier/clients/${e(clientId)}/sites`);
  return (j.sites || []).map((s) => ({ ...s, score: scoreLieu(s, texte) }))
    .sort((a, b) => b.score - a.score);
}

/** Capacités du tenant DE LA SESSION, lues à la source d'OmniHub quand le cache local est
 *  vide (application ouverte directement). ⚠️ SEUL le tenant courant du serveur
 *  (`current_tenant_slug`) fait foi — ⛔ repli sur « le premier » ou « par défaut ». */
export async function capacitesDeLaSession() {
  const j = await appel("/api/platform/user/tenants");
  const t = (j.tenants || []).find((x) => x.slug && x.slug === j.current_tenant_slug);
  return { profil: t?.profil_chantier || null, activite: t?.dossier_activite || null };
}

/** 🔴 N°1016 — TOUTES les visites d'un dossier (en cours ET closes), avec leur devis éventuel
 *  et ce qu'on peut en faire. LECTURE. ⚠️ L'accueil « Reprendre » ne liste que les visites non
 *  closes : une visite terminée dont le devis a été refusé y devenait introuvable. */
export async function visitesDuDossier(dossierId) {
  return appel(`/api/metier/mario/dossiers/${e(dossierId)}/visites`);
}

export { cleAdresse };

/** 🔴 P1 (démo Laurent) — le dossier n'a PAS d'adresse : on la crée dans OH depuis le parcours Mario, sur le SITE du client, puis on la rattache.
 *  ⛔ ANTI-DOUBLON : on cherche d'abord, chez le MÊME client (le serveur ne rend que ceux du tenant de la session), un site à l'adresse équivalente
 *  (normalisée) ⇒ RÉUTILISÉ plutôt que recréé. Routes EXISTANTES uniquement (sites). Écriture : appelée sur un clic explicite.
 *  Rend { site, reutilise }. Le serveur refuse de changer le lieu d'un dossier qui en a déjà un (422, message affiché). */
export async function ajouterAdresseAuDossier(dossierId, saisie, { rattacher = true, clientId = null } = {}) {
  const adresse = { adresse: String(saisie.adresse || "").trim(), ville: String(saisie.ville || "").trim(),
                    ...(String(saisie.code_postal || "").trim() ? { code_postal: String(saisie.code_postal).trim() } : {}) };
  if (!adresse.adresse || !adresse.ville) throw new Error("L'adresse et la ville sont obligatoires.");
  // `clientId` connu de l'appelant (ex. écran de modification du devis) ⇒ pas de relecture ; sinon relu depuis le dossier.
  const client_id = clientId || (await visitesDuDossier(dossierId)).dossier?.client_id;
  if (!client_id) throw new Error("Client du dossier introuvable.");
  const existants = await lieuxDuClient(client_id, "");
  let site = trouverSiteEquivalent(existants, adresse);
  const reutilise = Boolean(site);
  if (!site) site = await appel(`/api/metier/clients/${e(client_id)}/sites`, { methode: "POST", corps: adresse });
  // `rattacher: false` : le dossier a DÉJÀ son lieu (le serveur refuse d'en changer) — l'adresse est alors ajoutée aux adresses du CLIENT seulement.
  if (rattacher) await appel(`/api/metier/dossiers/${e(dossierId)}/site`, { methode: "POST", corps: { site_id: site.id } });
  return { site, reutilise, rattache: rattacher };
}

/** 🔴 P1 (organisation validée JJ : 1 SITE PAR DOSSIER) — l'adresse d'une visite est le SITE DE SON DOSSIER, relu au serveur, ⛔ pas le libellé `contexte.lieu` figé à l'ouverture.
 *  Rend l'adresse du site du dossier ; `null` quand le serveur n'en rend pas (dossier sans site, ou lecture impossible) : l'appelant garde alors ce qu'il affichait — ⛔ on n'efface jamais une
 *  adresse sur une absence de réponse. LECTURE. */
export async function lieuDuDossier(dossierId) {
  try {
    const { dossier } = await visitesDuDossier(dossierId);
    return dossier?.site_id && dossier.lieu ? dossier.lieu : null;
  } catch { return null; }
}

/** 🔴 P1 (lot S) — l'adresse du SITE du dossier, complète (pour la modifier), ou null si le dossier n'a pas de site. LECTURE. */
export async function siteDuDossier(dossierId) {
  const { dossier } = await visitesDuDossier(dossierId);
  return dossier?.site_id ? appel(`/api/metier/sites/${e(dossier.site_id)}`) : null;
}

/** 🔴 P1 (lot S) — MODIFIE l'adresse du site du dossier (PATCH de la route site existante : id + tenant, anti-doublon, devis émis non réécrits). Le rattachement du dossier ne change pas. Écriture sur clic explicite. */
export async function modifierAdresseDuDossier(dossierId, saisie) {
  const site = await siteDuDossier(dossierId);
  if (!site) throw new Error("Ce dossier n'a pas d'adresse à modifier : utilisez « Ajouter l'adresse ».");
  return appel(`/api/metier/sites/${e(site.id)}`, { methode: "PATCH", corps: {
    adresse: String(saisie.adresse || "").trim(), ville: String(saisie.ville || "").trim(), code_postal: String(saisie.code_postal || "").trim() } });
}

/** 🔴 N°1016 — rouvre une visite CLOSE dont aucun devis n'est issu (le serveur décide), sans
 *  rien perdre. Écriture : appelée sur un clic explicite. */
export async function rouvrirVisite(releveId) {
  return appel(`/api/metier/mario/sessions/${e(releveId)}/rouvrir`, { methode: "POST" });
}

/** 🔴 N°1021-A — accès SANS LOGIN (campagne de test, décision JJ) : le serveur décide du
 *  tenant et des routes ; fermé ⇒ `false` et l'écran garde le login actuel. */
export async function ouvrirAccesSansLogin() {
  try {
    const r = await fetch(`${API}/api/hub/auth/acces-ouvert-visite`, { method: "POST", credentials: "include" });
    if (!r.ok) return false;
    const j = await r.json().catch(() => ({}));
    if (!j.session_token) return false;
    localStorage.setItem("session_token", j.session_token);
    return true;
  } catch {
    return false;
  }
}

export async function mesVisitesEnCours() {
  return (await appel("/api/chantier/sessions/mes-en-cours")).releves || [];
}

/** 🔴 LE SEUL POINT D'ÉCRITURE — appelé APRÈS la confirmation explicite.
 *  lieu : { id } existant OU { nouveau: { adresse, ville, code_postal?, nom_site? } }
 *  dossier : { id } existant OU null (⇒ nouveau dossier sur ce lieu). */
export async function confirmer({ client, lieu, dossier, activite, profilCode, libelles }) {
  let lieuId = lieu.id;
  if (!lieuId) {
    // 🔴 P1 — ANTI-DOUBLON PARTAGÉ : un site équivalent de CE client est RÉUTILISÉ, jamais recréé (la lecture ne rend que le tenant de la session).
    const equivalent = trouverSiteEquivalent(await lieuxDuClient(client.id, ""), lieu.nouveau);
    lieuId = equivalent
      ? equivalent.id
      : (await appel(`/api/metier/clients/${e(client.id)}/sites`, { methode: "POST", corps: lieu.nouveau })).id;
  }
  let dossierId = dossier?.id;
  let reference = dossier?.reference;
  if (!dossierId) {
    const adr = lieu.nouveau || lieu;
    const d = await appel("/api/metier/dossiers", { methode: "POST", corps: {
      client_id: client.id, site_id: lieuId, activite,
      adresse_travaux: adr.adresse || undefined, code_postal_travaux: adr.code_postal || undefined,
      ville_travaux: adr.ville || undefined,
    } });
    const dd = d.dossier || d;
    dossierId = dd.id; reference = dd.reference;
  }
  // 🔴 P-MARIO-15 — LE MÊME mécanisme que « Nouvelle visite » de la fiche dossier :
  //    `apiBureau(dossier).ouvrir` (⛔ un second appel écrit ici). `contexte` rend
  //    « Reprendre » lisible SANS que `chantier` lise un autre domaine.
  const releve = await apiBureau(dossierId).ouvrir(profilCode,
    { client: libelles.client, lieu: libelles.lieu, dossier: reference || null });
  return { releve, dossierId, reference };
}
